import { useMemo, useState } from "react";
import Sheet from "../Sheet.jsx";
import EmptyState from "../EmptyState.jsx";
import { InlineMetricGrid } from "../PageHero.jsx";
import HierarchyMap from "./HierarchyMap.jsx";
import { DayBars, WhoActed } from "./RegisterAnatomy.jsx";
import { CompositionPanel } from "./Composition.jsx";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { GROUP_HUES, forgetRegisterReading, readingKeyFor, readingShareOf } from "../../lib/registerSnapshot.js";
import { hasSensitivePermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";

// RegisterReadings: a scoped readings file, the reading endpoint's answers precomputed, shown for the Explore scope
// the lens is on. The register reads the organization by the work people share, not by model, tool or topic, so a
// scoped reading is projected: where the scope's work sits in the register, the practices the register saw under
// the scope's own prompts, and the ones it carries from where that work sits. Selecting a part of the register on
// the map reads that part of the scope's work in the sheets below it.

// A share under half a percent reads "<1%", not "0%": the work is there, just little of it.
const pct = (x) => (x > 0 && x < 0.005 ? "<1%" : `${Math.round((x || 0) * 100)}%`);
const DIM_LABEL = { org: "Organization", models: "Model", mcp_servers: "MCP server", tools: "Tool", clusters: "Topic", endpoints: "Endpoint" };
const BUTTON_CLASS = "h-[24px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] text-[11px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] cursor-pointer";
const CONCERNS_SHOWN = 12;
const PEOPLE_SHOWN = 8;
const LINK_CLASS = "self-start justify-self-start border-0 bg-transparent p-0 text-[11px] leading-[1.1] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 transition-colors hover:text-[color:var(--ink)] cursor-pointer";

// A node's name is the run's own label unless a file carries a title for it: a group's label is its id.
const titled = (n) => Boolean(n?.name) && n.name !== n.id;

function nodeLabel(R, id) {
  const n = R.nodes.get(id);
  if (!n) return id;
  if (n.kind === "org") return "the organization";
  if (n.kind === "region") return titled(n) ? `${n.id} (${n.name})` : n.id;
  return `${n.id.split("/")[0]}: ${n.name}`;
}

function rowLabel(R, id, name) {
  return R.nodes.get(id)?.kind === "region" ? (name && name !== id ? `${id}: ${name}` : id) : `${id.split("/")[0]}: ${name}`;
}

function pathTo(R, id) {
  const path = [];
  const seen = new Set();
  for (let cur = id; cur && !seen.has(cur); cur = R.nodes.get(cur)?.parent || null) {
    seen.add(cur);
    path.unshift(cur);
  }
  return path;
}

/**
 * The part of a reading a selection reads: the part at the selected node, or the nearest one above it when too
 * little of this work sits there to read on its own. Null when nothing is selected.
 */
function focusOf(R, reading, sel) {
  if (!sel || sel === "org") return null;
  const parts = reading.parts || {};
  const holder = pathTo(R, sel).reverse().find((id) => parts[id]);
  return { sel, part: holder ? parts[holder] : null, exact: holder === sel };
}

function SummarySheet({ R, reading, source, kept, lastOnly, label }) {
  const { activity, coverage, scope } = reading;
  const b = R.build;
  const wholeSessions = coverage.grounded === "sessions";
  const readLine = coverage.read_windows
    ? `The register read ${formatCompact(coverage.read_sessions)} of these ${formatCompact(activity.sessions)} sessions, and ${coverage.read_windows} of its read windows fall ${wholeSessions ? "in them" : "under these prompts"}.`
    : coverage.read_sessions
      ? `The register read ${coverage.read_sessions} of these sessions, but none of its read windows fall under these prompts, so every practice here is one held where this work sits, not one seen in it.`
      : "The register never read this work, so every practice here is one held where it sits, not one seen in it.";
  return (
    <Sheet
      eyebrow={scope.dim === "org" ? "Register reading" : `Register reading for this ${(DIM_LABEL[scope.dim] || "scope").toLowerCase()}`}
      title={scope.dim === "org" ? "The organization" : label || scope.label}
      detail={[
        `Built over ${(b.window_start || "").slice(0, 10)} to ${(b.window_end || "").slice(0, 10)}. `,
        scope.dim === "org" ? "" : `The register reads the organization by the work people share, not by ${(DIM_LABEL[scope.dim] || "scope").toLowerCase()}; this reading follows where this work sits in it. `,
        lastOnly ? "It follows the last step of the scope. " : "",
        wholeSessions && scope.dim !== "org" ? "It reads every session this machine ran in the build's window. " : "",
        `${pct(coverage.placed)} of these ${coverage.unit === "sessions" ? "sessions" : "prompts"} place in the register. ${readLine}`,
        b.prompts_from && b.prompts_from > (b.window_start || "").slice(0, 10) ? ` Prompts are counted from ${b.prompts_from}, the oldest the prompt store still holds.` : "",
        source ? ` Opened from ${source}; ${kept ? "this browser keeps it for your account and never uploads it." : "it stays in this tab and is not uploaded."}` : "",
      ].join("")}
      actions={source ? (
        <button type="button" onClick={() => { forgetRegisterReading(); }} className={BUTTON_CLASS}>
          Forget this reading
        </button>
      ) : null}
    >
      <InlineMetricGrid
        items={[
          ["Prompts", formatCompact(activity.prompts), `${pct(activity.share_of_window)} of the window`],
          ["Sessions", formatCompact(activity.sessions), `${formatCompact(coverage.read_sessions)} read by the register`],
          ["People", formatCompact(activity.people), activity.people === 1 ? "one person" : "with work in it"],
          ["Placed", pct(coverage.placed), coverage.unplaced > 0.005 ? `${pct(coverage.unplaced)} in sessions no node holds` : "in sessions the register routes"],
        ]}
      />
    </Sheet>
  );
}

// Where the scope's work sits: the register sized by the organization's work and lit by this scope's. A band or a
// Groups or Domains row selects that part for the sheets below.
function WhereSheet({ R, reading, org, sel, onSelect }) {
  const activity = useMemo(() => new Map(Object.entries(reading.where.mass)), [reading]);
  const shareOf = useMemo(() => readingShareOf(R, org.where.mass, reading.where.mass), [R, org, reading]);
  // A file leaves out a band's mass under its floor, so a missing one is little work, not none.
  const bandPct = (m) => (m ? pct(m) : "<1%");
  const describe = (band) => ({
    title: band.name,
    detail: `${bandPct(reading.where.mass[band.id])} of this work, ${bandPct(org.where.mass[band.id])} of the organization's`,
  });
  const entries = (rows) => ({
    entries: rows.map((r) => ({
      id: r.node,
      label: rowLabel(R, r.node, r.name),
      activity: Math.round(r.share * reading.activity.prompts),
      share: Math.round(r.share * 100),
    })),
    total: rows.length,
  });
  const top = reading.where.groups[0];
  return (
    <Sheet
      eyebrow="Where this work sits in the register"
      title={reading.scope.dim === "org" ? `${reading.where.groups.length} groups` : top ? `${pct(top.share)} in ${top.node}` : "Nowhere the register reaches"}
      detail="Width is the organization's work, with room kept for every group and wherever this reading's work sits; brightness is this reading's. Groups and domains go by the register's own ids. Select a band or a row to read that part of this work below."
    >
      <HierarchyMap
        tree={R.tree}
        activity={activity}
        selectedId={sel || "org"}
        onSelect={(id) => onSelect(id === "org" ? null : id)}
        shareOf={shareOf}
        describe={describe}
        ariaLabel="Register map lit by this reading's work. Select a band to read that part of it below."
        emptyLabel="The reading places none of this work."
      />
      <div className="grid gap-x-[32px] gap-y-[14px] [grid-template-columns:repeat(2,minmax(0,1fr))] max-[900px]:grid-cols-1">
        <CompositionPanel title="Groups" data={entries(reading.where.groups)} scopeTotal={reading.activity.prompts} rows={7} onDrill={onSelect} />
        <CompositionPanel title="Domains" data={entries(reading.where.domains)} scopeTotal={reading.activity.prompts} rows={7} onDrill={onSelect} />
      </div>
    </Sheet>
  );
}

// The selection as a trail from the whole scope down to the selected node; every step but the last selects.
function Crumbs({ R, reading, id, onSelect }) {
  const path = pathTo(R, id);
  const labelOf = (nid) => (nid === "org" ? (reading.scope.dim === "org" ? "Organization" : reading.scope.label) : R.nodes.get(nid)?.kind === "region" ? nid : R.nodes.get(nid)?.name || nid);
  return (
    <nav aria-label="Which part of this work" className="flex min-w-0 flex-wrap items-center gap-[4px] text-[11px]">
      {path.map((nid, i) => {
        const last = i === path.length - 1;
        return (
          <span key={nid} className="inline-flex min-w-0 items-center gap-[4px]">
            {last ? (
              <span className={`${EYEBROW_CLASS} truncate`}>{labelOf(nid)}</span>
            ) : (
              <button type="button" onClick={() => onSelect(nid === "org" ? null : nid)} className={`${EYEBROW_CLASS} truncate hover:text-[color:var(--green)] cursor-pointer`}>
                {labelOf(nid)}
              </button>
            )}
            {last ? null : <span aria-hidden="true" className="text-[color:var(--muted-soft)]">/</span>}
          </span>
        );
      })}
    </nav>
  );
}

// Who does the work and with what: the people, models, tools and MCP servers under the scope's own prompts,
// what agents did there and who acted, and prompts per day by the group each session routes to. Inside a selected
// part each session counts by the share of its work that routes there.
function WhoSheet({ R, reading, focus, onSelect }) {
  const src = focus?.part || reading;
  const w = src.who;
  const [everyone, setEveryone] = useState(false);
  const b = R.build;
  const groups = b.groups || [];
  const dayR = useMemo(() => {
    const hue = new Map(groups.map((g, i) => [g, GROUP_HUES[i % GROUP_HUES.length]]));
    hue.set("unplaced", "var(--line-strong)");
    return { days: b.days || [], groupIds: [...groups, "unplaced"], hue, nodes: {} };
  }, [b, groups]);
  if (!w) return null;
  const col = (rows, labelOf = (r) => r.label) => ({
    entries: rows.map((r) => ({ id: r.id, label: labelOf(r), activity: r.prompts, share: Math.round(r.share * 100) })),
    total: rows.length,
  });
  const n = w.distinct || {};
  const people = {
    entries: w.people.map((r) => ({ id: r.id, label: r.dept ? `${r.label} (${r.dept})` : r.label, activity: r.sessions ?? r.prompts, share: Math.round(r.share * 100) })),
    total: w.people.length,
  };
  const counts = `${formatCompact(n.people || 0)} ${n.people === 1 ? "person" : "people"}, ${formatCompact(n.models || 0)} ${n.models === 1 ? "model" : "models"}, ${formatCompact(n.tools || 0)} ${n.tools === 1 ? "tool" : "tools"}`;
  const total = focus?.part ? focus.part.activity.prompts : reading.activity.prompts;
  const maxAct = Math.max(1, ...w.acts.map((a) => a.n));
  const whole = reading.scope.dim === "org" ? "Read the whole organization" : "Read all of this scope";
  const selName = focus ? R.tree.byId.get(focus.sel)?.name || focus.sel : null;
  const detail = !focus
    ? reading.coverage?.grounded === "sessions"
      ? "People are counted by session, the rest by the prompts in these sessions. What agents did and who acted come from the register's reading of them. Explore breaks this scope down by topic."
      : "People are counted by session, the rest by this scope's own prompts. What agents did and who acted come from the register's reading of the turns under them. Explore breaks this scope down by topic."
    : [
      focus.part && !focus.exact ? `Too little of this work sits in ${selName} to read it on its own, so this reads ${R.tree.byId.get(focus.part.node)?.name}, which holds it. ` : "",
      !focus.part ? `Too little of this work sits in ${selName} to read it on its own (${pct(reading.where.mass[focus.sel])}), so the sheets below read all of it. ` : "",
      focus.part ? `${pct(focus.part.share)} of this work sits here: ${formatCompact(focus.part.activity.prompts)} prompts in ${formatCompact(focus.part.activity.sessions)} sessions, ${counts}. A session counts by the share of its work that routes here, and what agents did comes from this part's own read windows.` : "",
    ].join("");
  return (
    <Sheet
      eyebrow={focus ? <Crumbs R={R} reading={reading} id={focus.sel} onSelect={onSelect} /> : "Who does this work, and with what"}
      title={focus?.part ? R.tree.byId.get(focus.part.node)?.name || focus.part.node : counts}
      detail={detail}
      actions={focus ? <button type="button" onClick={() => onSelect(null)} className={BUTTON_CLASS}>{whole}</button> : null}
    >
      <DayBars R={dayR} node={{ id: "scope", daily: { by_group: w.daily || {} } }} unit="prompts" />
      <div className="grid gap-x-[32px] gap-y-[14px] pt-[4px] [grid-template-columns:repeat(4,minmax(0,1fr))] max-[1100px]:[grid-template-columns:repeat(2,minmax(0,1fr))]">
        <div className="grid content-start gap-[8px]">
          <CompositionPanel title="People" data={people} scopeTotal={w.sessions ?? total} rows={everyone ? w.people.length : PEOPLE_SHOWN} />
          {w.people.length > PEOPLE_SHOWN ? (
            <button type="button" aria-expanded={everyone} onClick={() => setEveryone((v) => !v)} className={LINK_CLASS}>
              {everyone ? "Show fewer" : `Show all ${w.people.length}`}
            </button>
          ) : null}
        </div>
        <CompositionPanel title="Models" data={col(w.models)} scopeTotal={total} rows={8} />
        <div className="grid content-start gap-[12px]">
          <CompositionPanel
            title="What agents did"
            data={{ entries: w.acts.map((a) => ({ id: a.act, label: a.act, activity: a.n, share: Math.round((a.n / maxAct) * 100) })), total: w.acts.length }}
            rows={6}
          />
          <WhoActed actors={w.actors} />
        </div>
        <div className="grid content-start gap-[12px]">
          <CompositionPanel title="Tools" data={col(w.tools)} scopeTotal={total} rows={5} />
          <CompositionPanel title="MCP servers" data={col(w.mcp_servers)} scopeTotal={total} rows={4} />
        </div>
      </div>
    </Sheet>
  );
}

function Mark({ kind }) {
  const base = "mt-[5px] inline-block h-[8px] w-[8px] shrink-0 rounded-full";
  if (kind === "observed") return <span aria-hidden="true" className={`${base} bg-[color:var(--green)]`} />;
  if (kind === "carried") return <span aria-hidden="true" className={`${base} border border-[color:var(--muted)]`} />;
  return <span aria-hidden="true" className={`${base} bg-[color:var(--line-strong)]`} />;
}

function PracticeRow({ R, r, list }) {
  const meta = list === "observed"
    ? `Seen here: ${r.observed_windows} of its ${r.windows} windows fall in this work, ${r.observed_people} ${r.observed_people === 1 ? "person" : "people"}. Held at ${nodeLabel(R, r.node)}.`
    : list === "carried"
      ? `Not seen in this work, but held where ${pct(r.weight)} of it sits, ${r.lift}x the organization's share. Held at ${nodeLabel(R, r.node)}.`
      : r.stated_at && r.stated_at.length > 1
        ? `Held across the organization, and stated more specifically at ${r.stated_at.filter((n) => n !== "org").slice(0, 3).map((n) => nodeLabel(R, n)).join(", ")}.`
        : "Held across the organization.";
  return (
    <div role="listitem" className="flex gap-[9px] border-t border-[color:var(--line-soft)] py-[7px] first:border-t-0">
      <Mark kind={list} />
      <div className="grid min-w-0 gap-[2px]">
        <span className="text-[12px] leading-[1.38] text-[color:var(--ink)]">
          <span className="mr-[6px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{r.kind}</span>
          {list === "org" && r.org_statement ? r.org_statement : r.statement}
        </span>
        <span className={NOTE_CLASS}>{meta}</span>
      </div>
    </div>
  );
}

function PracticeList({ R, title, note, rows, list, empty }) {
  return (
    <div className="grid content-start gap-[4px]">
      <div className="flex items-baseline justify-between gap-[8px] border-b border-[color:var(--line-soft)] pb-[4px]">
        <span className={EYEBROW_CLASS}>{title}</span>
        <span className={`${NOTE_CLASS} text-[10px]`}>{note}</span>
      </div>
      {rows.length ? (
        <div role="list">{rows.map((r) => <PracticeRow key={r.practice} R={R} r={r} list={list} />)}</div>
      ) : (
        <span className={`${NOTE_CLASS} block py-[6px]`}>{empty}</span>
      )}
    </div>
  );
}

function HowSheet({ R, reading, focus, gatedButAllowed }) {
  const part = focus?.part || null;
  const all = (part || reading).practices;
  // Everything the register holds for the organization, or for a part of it, it read in that work's own sessions: a
  // practice there is always seen, so the organization's sheet lists what was seen and what the organization states.
  const orgReading = reading.scope.dim === "org";
  const [kind, setKind] = useState("norm");
  const only = (rows) => rows.filter((r) => r.kind === kind);
  const p = { observed: only(all.observed), carried: only(all.carried), organization: only(all.organization) };
  const c = all.counts.by_kind?.[kind] || { all: 0, observed: 0, carried: 0 };
  const word = kind === "norm" ? "norms" : "intents";
  const here = part ? R.tree.byId.get(part.node)?.name || part.node : null;
  const title = orgReading
    ? part ? `${c.observed} ${word} seen here` : `${c.all} ${word}`
    : `${c.observed} ${word} seen in this work, ${c.carried} held where it sits`;
  const held = "Held where it sits: the practice holds in the part of the register this work sits in, more than across the organization, but the register has not seen it in this work.";
  const detail = orgReading
    ? part
      ? "The practices the register saw in this part's own read windows, most concentrated here first, and what the organization states for all of its work."
      : "The practices the register holds across the organization, most seen first."
    : part
      ? "Seen: the register cited the practice in this part's own read windows, in this scope's work. Held where it sits: the practice holds in this part, more than across the organization, but the register has not seen it in this part of the work."
      : reading.coverage?.grounded === "sessions"
        ? `Seen: the register cited the practice in one of these sessions. ${held}`
        : `Seen: a turn the register cited for the practice falls under one of this scope's own prompts. ${held}`;
  return (
    <Sheet
      eyebrow={here ? `How this work is done in ${here}` : "How this work is done"}
      title={title}
      actions={
        <div className="flex gap-[2px]" role="tablist" aria-label="Norms or intents">
          {[["norm", "Norms"], ["intent", "Intents"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              onClick={() => setKind(k)}
              className={[
                "h-[24px] rounded-[2px] border px-[8px] text-[11px] cursor-pointer",
                kind === k ? "border-[color:var(--line-strong)] bg-[color:var(--bg)] text-[color:var(--ink)]" : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)]",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
      }
      detail={[
        detail,
        gatedButAllowed ? " One machine's practices read as one person's conduct; they show here because your role can read session content." : "",
      ].join("")}
    >
      <div className="grid gap-x-[32px] gap-y-[18px] [grid-template-columns:repeat(2,minmax(0,1fr))] max-[1000px]:grid-cols-1">
        <PracticeList R={R} title="Seen in this work" note="most concentrated here first" rows={p.observed.slice(0, 8)} list="observed" empty="The register read none of this work's turns." />
        {orgReading ? (
          <PracticeList R={R} title="Organization-wide" note="held at the organization" rows={p.organization} list="org" empty="" />
        ) : (
          <PracticeList R={R} title="Held where it sits" note="not seen in this work; most over-represented first" rows={p.carried.slice(0, 8)} list="carried" empty="Nothing holds here beyond the organization's own practices." />
        )}
      </div>
      {orgReading ? null : <PracticeList R={R} title="Organization-wide" note="held at the organization" rows={p.organization.slice(0, 4)} list="org" empty="" />}
    </Sheet>
  );
}

function TensionIcon({ conflict }) {
  return (
    <span aria-hidden="true" className="inline-flex h-[12px] w-[12px] items-center justify-center" style={{ color: conflict ? "var(--ink)" : "var(--severity-medium)" }}>
      {conflict ? (
        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1.5 4h8M7.5 1.8 9.7 4 7.5 6.2M10.5 8h-8M4.5 5.8 2.3 8l2.2 2.2" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 1.4 11 10.4H1z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /><path d="M6 4.6v2.7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /><circle cx="6" cy="8.8" r=".7" fill="currentColor" /></svg>
      )}
    </span>
  );
}

// The concerns near this work: the practices the register's writer marked uncomfortable, seen in this work or where
// enough of it sits, and any conflicts the roll-up recorded between groups.
function ConcernsSheet({ R, reading, focus }) {
  const part = focus?.part || null;
  const rows = (part || reading).concerns || [];
  const conflicts = (reading.tensions || []).filter((t) => !part || t.node === part.node);
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, CONCERNS_SHOWN);
  const seen = rows.filter((r) => r.observed).length;
  const everything = reading.scope.dim === "org";
  const here = part ? R.tree.byId.get(part.node)?.name || part.node : null;
  const noun = (k) => `${k} ${k === 1 ? "concern" : "concerns"}`;
  const title = !rows.length && !conflicts.length
    ? "No concerns near this work"
    : [
      conflicts.length ? `${conflicts.length} ${conflicts.length === 1 ? "conflict" : "conflicts"}` : null,
      rows.length ? (everything ? noun(rows.length) : `${noun(seen)} seen in this work, ${rows.length - seen} where it sits`) : null,
    ].filter(Boolean).join(", ");
  const meta = (r) => (everything
    ? `Seen in ${nodeLabel(R, r.node)}.`
    : r.observed
      ? `Seen in this work, in ${nodeLabel(R, r.node)}.`
      : `Seen in ${nodeLabel(R, r.node)}, where ${pct(r.share)} of this work sits.`);
  return (
    <Sheet
      eyebrow={here ? `Concerns in ${here}` : "Concerns near this work"}
      title={title}
      detail={[
        "Practices the register's writer marked uncomfortable: ones the organization may not have written down or may not like, such as a gate skipped, an approval assumed, a credential in a command or a rule worked around. Each rests on one window, is listed in the writer's words, and is folded into a broader practice above.",
        R.build.conflicts_recorded === false ? " The roll-up that built this register recorded no conflicts between groups." : "",
      ].join("")}
    >
      {/* Balanced columns, as many as fit: a long list stays a short band. */}
      <div role="list" className="columns-[300px] gap-x-[24px]">
        {conflicts.map((t, i) => (
          <div key={`t${i}`} role="listitem" className="grid break-inside-avoid gap-[3px] border-t border-[color:var(--line-soft)] py-[7px]">
            <span className="flex items-center gap-[6px] text-[10px] uppercase tracking-[0.06em]">
              <TensionIcon conflict={t.kind === "conflict"} />
              <span className="text-[color:var(--eyebrow)]">{t.kind === "conflict" ? "Conflict" : "Concern"}</span>
              <span className="text-[color:var(--muted-soft)]">{t.node === "org" ? "organization" : t.node}</span>
            </span>
            <span className="text-[12px] leading-[1.38] text-[color:var(--ink)]">{t.text}</span>
          </div>
        ))}
        {shown.map((r) => (
          <div key={r.id || r.concern} role="listitem" className="grid break-inside-avoid gap-[3px] border-t border-[color:var(--line-soft)] py-[7px]">
            <span className="flex items-center gap-[6px] text-[10px] uppercase tracking-[0.06em]">
              <TensionIcon conflict={false} />
              <span className="text-[color:var(--eyebrow)]">Concern</span>
              {r.act ? <span className="min-w-0 truncate text-[color:var(--muted-soft)]">{r.act}</span> : null}
            </span>
            <span className="text-[12px] leading-[1.38] text-[color:var(--ink)]">{r.statement}</span>
            <span className={NOTE_CLASS}>{meta(r)}</span>
          </div>
        ))}
      </div>
      {rows.length > CONCERNS_SHOWN ? (
        <button
          type="button"
          aria-expanded={all}
          onClick={() => setAll((v) => !v)}
          className={LINK_CLASS}
        >
          {all ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      ) : null}
    </Sheet>
  );
}

export default function RegisterReadings({ R, scopePath = [], source = null, kept = false }) {
  // One machine's people, practices and concerns read as one person's conduct: they show to a role that can read
  // session content (an admin's does), by the same check as the dashboard's other session content, which keeps them
  // hidden while the viewer's permissions are loading or degraded.
  const caps = useMyPermissions();
  const canReadPeople = hasSensitivePermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const key = readingKeyFor(scopePath);
  const found = R.readings[key];
  const reading = found || R.readings.org;
  const org = R.readings.org;
  const last = scopePath.length ? scopePath[scopePath.length - 1] : null;
  // The selection belongs to the reading it was made in, so moving to another scope starts from all of its work.
  const [pick, setPick] = useState({ key: reading.scope.key, sel: null });
  const sel = pick.key === reading.scope.key ? pick.sel : null;
  const onSelect = (id) => setPick({ key: reading.scope.key, sel: id || null });
  const focus = focusOf(R, reading, sel);
  const hidden = reading.role_gated && !canReadPeople;
  return (
    <div className="grid gap-[12px]">
      {!found && last ? (
        <EmptyState
          title="This scope has no reading in the file"
          note={`The file holds readings for ${R.index.length} scopes (models, MCP servers, tools, topics and endpoints with enough activity). ${last.label || last.id} is not one of them, so the organization's reading shows below.`}
        />
      ) : null}
      <SummarySheet R={R} reading={reading} source={source} kept={kept} lastOnly={Boolean(found && scopePath.length > 1)} label={found && last?.label && last.label !== last.id ? last.label : null} />
      <WhereSheet R={R} reading={reading} org={org} sel={sel} onSelect={onSelect} />
      {hidden ? (
        <EmptyState
          title="This machine's people, practices and concerns are hidden by your organization's data-access policy"
          note="An endpoint is usually one person's machine, so its people, practices and concerns read as that person's conduct, and your role does not include permission to read session content. The map above still shows where the work sits."
        />
      ) : (
        <>
          <WhoSheet key={`who:${reading.scope.key}:${focus?.part?.node || ""}`} R={R} reading={reading} focus={focus} onSelect={onSelect} />
          <HowSheet R={R} reading={reading} focus={focus} gatedButAllowed={reading.role_gated} />
          <ConcernsSheet key={`concerns:${reading.scope.key}:${focus?.part?.node || ""}`} R={R} reading={reading} focus={focus} />
        </>
      )}
    </div>
  );
}
